
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter } from "react-router-dom";
import { matchPath, useLocation } from "react-router-dom";
import { UnifiedAuthProvider } from "@/contexts/auth/UnifiedAuthProvider";
import { SubscriptionProvider } from "@/contexts/SubscriptionContext";
import { SessionProvider } from "@/contexts/SessionContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { ConsentProvider } from "@/contexts/ConsentContext";
import ConsentUI from "@/components/consent/ConsentUI";
import { ErrorBoundary } from "@/components/error-boundary/ErrorBoundary";
import AppRoutes from "./AppRoutes";
import Footer from "./components/Footer";
import TopProgressBar from "@/components/ui/TopProgressBar";
import AuthStatusBanner from "@/components/security/AuthStatusBanner";
import { RouteTracker } from "@/components/analytics/RouteTracker";
import { AuthListener } from "@/components/analytics/AuthListener";
import MetaPixelIdentitySync from "@/components/analytics/MetaPixelIdentitySync";
import React from "react";
import { logger } from '@/lib/logger';
import { queryClient } from "@/lib/query-client";

// Dev-only: React Query DevTools (lazy loaded to avoid prod bundle impact)
const Devtools = import.meta.env.DEV
  ? React.lazy(() =>
      import('@tanstack/react-query-devtools').then((mod) => ({
        default: mod.ReactQueryDevtools
      }))
    )
  : null;

interface QueryErrorType {
  status?: number;
  message?: string;
}

/**
 * Whether the app-wide <Footer /> should render for a given pathname.
 *
 * - "/" (sales landing) has its own footer.
 * - "/session/:threadId" (live chat, SessionChat) is a fixed-height app shell;
 *   an extra footer below it makes the document scroll, which carries the
 *   session header and End Session off the top of the screen.
 *   "/session/new" also matches that pattern but renders SessionSetup, so it
 *   keeps the footer, as do the nested /session/:threadId/* routes.
 */
export function shouldShowAppFooter(pathname: string): boolean {
  const isSalesLanding = pathname === "/";
  const isLiveSessionChat =
    pathname !== "/session/new" &&
    matchPath({ path: "/session/:threadId", end: true }, pathname) !== null;

  return !isSalesLanding && !isLiveSessionChat;
}

function AppShell() {
  const location = useLocation();
  const showAppFooter = shouldShowAppFooter(location.pathname);

  return (
    <div className="min-h-screen flex flex-col">
      <RouteTracker />
      <AuthListener />
      <MetaPixelIdentitySync />
      <TopProgressBar />
      <AppRoutes />
      <AuthStatusBanner />
      {/* The sales landing includes its own Manus-matching footer, and the live chat
          page is a fixed-height shell; keep the app footer everywhere else */}
      {showAppFooter ? <Footer /> : null}
    </div>
  );
}

function App() {
  if (import.meta.env.DEV && typeof window !== 'undefined') {
    document.addEventListener('securitypolicyviolation', (e) => {
      logger.error('CSP violation:', {
        blockedURI: e.blockedURI,
        violatedDirective: e.violatedDirective,
        originalPolicy: e.originalPolicy
      });
    });
  }

  return (
    <>
      <ErrorBoundary>
        <QueryClientProvider client={queryClient}>
          <ThemeProvider>
            <ConsentProvider>
            <TooltipProvider>
              <Toaster />
              <Sonner />
              <BrowserRouter>
                <UnifiedAuthProvider>
                  <SubscriptionProvider>
                    <SessionProvider>
                      <AppShell />
                      {import.meta.env.DEV && Devtools ? (
                        <React.Suspense fallback={null}>
                          <Devtools initialIsOpen={false} buttonPosition="bottom-left" />
                        </React.Suspense>
                      ) : null}
                    </SessionProvider>
                  </SubscriptionProvider>
                </UnifiedAuthProvider>
              </BrowserRouter>
            </TooltipProvider>
            <ConsentUI />
            </ConsentProvider>
          </ThemeProvider>
        </QueryClientProvider>
      </ErrorBoundary>
    </>
  );
}

export default App;
